/**
 * Layer 3 formation anchors. (§8.2, §10.1 Layer 3)
 *
 * The roster the match is built with IS the formation: an 11v11 built as
 * GK/CB/CB/LB/RB/LM/RM/CM/CM/ST/ST is a 4-4-2, a 7-a-side as GK/CB/CB/CM/LM/RM/ST
 * is a 3-3-1, and so on. So rather than re-deriving a formation from squadSize,
 * we map each player's Position (§8.2) to a normalized anchor and let the roster
 * pick the shape. This is what "sensible smaller-mode shapes" means in practice:
 * the same table produces a coherent block at any squad size, because smaller
 * modes simply use fewer of the same positions.
 *
 * Normalized coordinates:
 *   fx ∈ [-1, +1]  along the team's OWN attack axis: -1 = on top of own goal,
 *                  +1 = on the opponent's goal line.
 *   fz ∈ [-1, +1]  across the pitch: sign is a fixed left/right, resolved to a
 *                  world side per team so both teams mirror correctly.
 *
 * ── How this fits Layer 3's space analysis (§10.1) ──────────────────────────
 * The brief's Layer 3 also calls for a Voronoi / influence-map space analysis
 * over a 32×20 grid to find the best support/receiving cell. That IS implemented
 * — see influence.ts (InfluenceMap) and its use in team-ai.ts / individual.ts.
 * These anchors are the static positional table that the influence-map search
 * complements: the anchor says where a role belongs, the influence map says
 * where the open space near it is, and the solver in team-ai.ts blends the two.
 */

import type { Position } from '../sim/types.js';

// The anchor table itself moved to sim/formation-anchors.ts — the rules FSM
// now stages the kickoff shape from it, and sim/ must not import from ai/.
// Re-exported here so every existing ai-side import keeps working unchanged.
export { POSITION_ANCHORS, type Anchor } from '../sim/formation-anchors.js';

/** Attackers push furthest forward on a team's turn / drop deepest when defending. */
export function isForwardPosition(pos: Position): boolean {
  return pos === 'ST' || pos === 'CF' || pos === 'LW' || pos === 'RW' || pos === 'CAM';
}

export function isDefenderPosition(pos: Position): boolean {
  return pos === 'CB' || pos === 'LB' || pos === 'RB' || pos === 'LWB' || pos === 'RWB' || pos === 'GK';
}

/** Fullbacks/wing-backs — defenders who overlap up the wing when their team attacks. */
export function isFullbackPosition(pos: Position): boolean {
  return pos === 'LB' || pos === 'RB' || pos === 'LWB' || pos === 'RWB';
}
